import {
  PanelFragment,
  QualifiedFeature,
  QualifiedFeatureFragment,
  QualifiedFeatureQualifiers,
  Qualifier,
} from "fe-common/graphql";
import { PatientCaseGrouping } from "../context/diagnosticAgent";
import { Relations, Values } from "shared";
import { Suggestion } from "./diagnosticAgent/hooks/useSuggestions";
import { getQualifierByRelation } from "fe-common/logic";
import {
  PatientCaseChange,
  PatientCaseOp,
} from "./diagnosticAgent/usePatientCase";

export enum LevelValue {
  HIGH = "high",
  LOW = "low",
  NORMAL = "normal",
}
export interface DataInput {
  negate?: boolean;
  level?: LevelValue;
  qualifiers?: Qualifier[];
  value?: number;
}

export interface DataInputPanel extends Partial<PanelFragment> {
  title: string;
  id: string;
  findings?: QualifiedFeature[];
  customFindings?: QualifiedFeature[];
  suggestions?: Suggestion[];
}

//Those are features that when calculating the hash, we need to consider the qualifiers for them as well
//For example exposure_to id is the same for all features in the Medications panel, to distinguish between them we need the qualifiers
//Which is included in the qf.id

const FEATURES_TO_CONSIDER_QUALIFIERS_FOR_HASH = [
  Relations.FAMILY_HISTORY,
  Relations.EXPOSURE_TO,
  Relations.GEOGRAPHIC,
  Relations.ETHNIC_GROUP,
  Relations.OCCUPATION,
  Relations.EXPOSURE_TO,
  Relations.TRAVEL_HISTORY,
  Relations.PROCEDURE_FINDING,
];

// utility functions for the DataInputResponses structure
/**
 * a hash id which ignores level and negate, so reponses to the same item will match
 * @param qf
 */

function hashForQualifiedFeatureResponse(qf: QualifiedFeature): string {
  const qualifierToId = (q: Qualifier) => {
    let range = null;
    if (q.range && q.range.high)
      range = `${q.range.low}_${q.range.high}_${q.range.units}`;
    return q.relation.id + "_" + (range || q.value?.id);
  };

  const qualifiersIDs = qf.qualifiers
    ?.filter((q) =>
      FEATURES_TO_CONSIDER_QUALIFIERS_FOR_HASH.includes(
        q.relation.id as Relations,
      ),
    )
    .map(qualifierToId)
    .join(",");

  return qf.feature.id + "_" + qualifiersIDs;
}

export const sameDataInputQualifiedFeature = (
  a: QualifiedFeature,
  b: QualifiedFeature,
) => {
  return (
    hashForQualifiedFeatureResponse(a) === hashForQualifiedFeatureResponse(b)
  );
};

export const sameQualifers = (q1: Qualifier, q2: Qualifier): boolean =>
  q1.relation.id === q2.relation.id &&
  q1.value?.id === q2.value?.id &&
  q1.range?.low === q2.range?.low &&
  q1.range?.high === q2.range?.high &&
  q1.range?.units === q2.range?.units &&
  !!q1.negate === !!q2.negate;

export const dedupQualifiedFeatures = (list: QualifiedFeature[]) => [
  ...new Map(
    list.map((qf) => [hashForQualifiedFeatureResponse(qf), qf]),
  ).values(),
];

/**
 * qf which are in list2 but not in list1
 * @param list1
 * @param list2
 */
export const disjointQualifiedFeatures = (
  list1: QualifiedFeature[],
  list2: QualifiedFeature[],
) => {
  const set1 = new Set(list1.map((l) => hashForQualifiedFeatureResponse(l)));
  return list2.filter((qf) => !set1.has(hashForQualifiedFeatureResponse(qf)));
};
type ResponseValue = {
  qf: QualifiedFeature;
  response: DataInput;
  section: PatientCaseGrouping;
};

function toLevelQualifierValue(l: LevelValue) {
  switch (l) {
    case LevelValue.HIGH:
      return Values.HIGH;
    case LevelValue.NORMAL:
      return Values.NORMAL;
    case LevelValue.LOW:
      return Values.LOW;
  }
}

export function mergeResponseToQF(qf: QualifiedFeatureFragment, r: DataInput) {
  if (!r) {
    return qf;
  }

  let merged = { ...qf, negate: r.negate };

  if (r.qualifiers) {
    merged.qualifiers = merged.qualifiers
      ?.filter(
        (q) =>
          q.relation.id === Relations.LEVEL ||
          q.relation.id === Relations.PANEL_ID,
      )
      .concat(r.qualifiers);
  }

  if (r.level) {
    merged.qualifiers =
      merged.qualifiers?.filter((q) => q.relation.id !== Relations.LEVEL) || [];
    let qualifier = {
      relation: { id: Relations.LEVEL, name: "level" },
      value: { id: toLevelQualifierValue(r.level), name: r.level },
    } as QualifiedFeatureQualifiers;
    merged.qualifiers.push(qualifier);
    merged.mandatory = qualifier;
  }

  return merged as QualifiedFeatureFragment;
}

export function responsesAsChangeOperation(...responses: ResponseValue[]) {
  return responses.map(
    ({ qf, section, response }) =>
      ({
        op:
          response != null
            ? PatientCaseOp.addToSection
            : PatientCaseOp.deleteFromSection,
        qf: response ? mergeResponseToQF(qf, response) : qf,
        section,
      } as PatientCaseChange),
  );
}

export function extractResponseFromQF(qf: QualifiedFeatureFragment): DataInput {
  if (!qf || qf.skip) return null;

  // level
  const levelValue = getQualifierByRelation(qf, Relations.LEVEL);
  const negate = qf?.negate;

  //qualifiers
  const qualifiers = qf.qualifiers.filter(
    (q) =>
      q.relation.id !== Relations.PANEL_ID && q.relation.id !== Relations.LEVEL,
  );

  return {
    negate,
    level:
      levelValue &&
      levelValue.value &&
      fromLevelQualifierValue(levelValue.value.id as Values),
    qualifiers,
  };
}

function fromLevelQualifierValue(l: Values) {
  switch (l) {
    case Values.HIGH:
      return LevelValue.HIGH;
    case Values.NORMAL:
      return LevelValue.NORMAL;
    case Values.LOW:
      return LevelValue.LOW;
  }
}
